import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Sun, CalendarDays, ArrowRight, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '../../ui/popover';
import TimeSelect from '../../ui/TimeSelect';
import { getQuickDates } from '../../../lib/constants';
import { useDateTime } from '../../../contexts/DateTimeContext';

// A task's day (due_date: "YYYY-MM-DD", or a datetime from older data) as a local date:
// new Date("2026-11-03") is midnight in UTC, the day before west of Greenwich.
export const dayOf = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * The one date picker for a task's day, everywhere a task's date is set (task detail, the
 * tasks table, the inline new-task row, bulk edits...): quick days, a month and a time.
 * `keepFocus`: the typing stays where it is while picking (the inline row closes when its
 * empty field loses the focus).
 */
const DatePicker = ({ value, onChange, timeValue, onTimeChange, children, disabled = false, keepFocus = false, align = 'start' }) => {
  const [open, setOpen] = useState(false);
  const day = dayOf(value);
  const [calendarMonth, setCalendarMonth] = useState(() => day || new Date());
  const openChange = (next) => {
    if (next) setCalendarMonth(dayOf(value) || new Date());
    setOpen(next);
  };
  const { t } = useTranslation();
  const { formatDate, formatDayOfWeek, formatMonthYear, weekdayLetters } = useDateTime();
  const quickDates = getQuickDates();

  const getCalendarDays = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    const days = [];
    for (let i = 0; i < startPadding; i++) days.push(null);
    for (let i = 1; i <= lastDay.getDate(); i++) days.push(new Date(year, month, i));
    return days;
  };

  const selectDate = (date) => {
    onChange(date);
    setOpen(false);
  };

  const defaultTrigger = (
    <button disabled={disabled} className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[13px] transition-colors ${
      disabled ? 'opacity-50 cursor-not-allowed' :
      value ? 'text-green-400 bg-green-500/10' : 'text-muted-foreground hover:bg-accent/60'
    }`}>
      <Calendar className="w-3.5 h-3.5" />
      <span>{day ? formatDate(day, true) : t('tasks.no_date')}</span>
      {value && !disabled && (
        <X className="w-3 h-3 ml-0.5 hover:text-foreground" onClick={(e) => { e.stopPropagation(); onChange(null); if (onTimeChange) onTimeChange(''); }} />
      )}
    </button>
  );

  return (
    <Popover open={open} onOpenChange={openChange}>
      <PopoverTrigger asChild disabled={disabled}>
        {children || defaultTrigger}
      </PopoverTrigger>
      <PopoverContent
        // As tall as the room there is: next to a row low in a long list (a table's due date) it
        // fit neither below nor above, and its top, with Today and Tomorrow, was off the screen.
        className="w-[272px] p-0 bg-accent border-border max-h-[var(--radix-popover-content-available-height)] overflow-y-auto max-md:!w-screen max-md:!max-w-none max-md:!rounded-b-none"
        align={align}
        sideOffset={4}
        collisionPadding={8}
        onClick={(e) => e.stopPropagation()}
        {...(keepFocus ? {
          onOpenAutoFocus: (e) => e.preventDefault(),
          onCloseAutoFocus: (e) => e.preventDefault(),
          // Picking a day keeps the focus in the field being typed (not on the time list).
          onMouseDown: (e) => { if (!e.target.closest('input, select, [role="combobox"], [role="listbox"]')) e.preventDefault(); },
        } : {})}
      >
        <div className="p-3">
          {/* Quick dates */}
          <div className="space-y-0.5 mb-3">
            {Object.entries(quickDates).map(([key, { date, labelKey }]) => (
              <button
                key={key}
                onClick={() => selectDate(date)}
                className="w-full flex items-center justify-between px-2.5 py-1.5 rounded-md hover:bg-accent/60 text-sm"
              >
                <div className="flex items-center gap-2.5">
                  {key === 'today' && <Sun className="w-3.5 h-3.5 text-green-400" />}
                  {key === 'tomorrow' && <Sun className="w-3.5 h-3.5 text-orange-400" />}
                  {key === 'laterThisWeek' && <CalendarDays className="w-3.5 h-3.5 text-blue-400" />}
                  {key === 'thisWeekend' && <CalendarDays className="w-3.5 h-3.5 text-purple-400" />}
                  {key === 'nextWeek' && <ArrowRight className="w-3.5 h-3.5 text-violet-400" />}
                  <span className="text-foreground/80">{t(labelKey)}</span>
                </div>
                <span className="text-muted-foreground/70 text-xs">{formatDayOfWeek(date, 'short')}</span>
              </button>
            ))}
            <button onClick={() => { onChange(null); if (onTimeChange) onTimeChange(''); setOpen(false); }} className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md hover:bg-accent/60 text-sm text-muted-foreground">
              <div className="w-3.5 h-3.5 rounded-full border border-input" />
              <span>{t('tasks.no_date')}</span>
            </button>
          </div>

          {/* Calendar */}
          <div className="border-t border-border pt-3">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-xs text-foreground/80">
                {formatMonthYear(calendarMonth)}
              </span>
              <div className="flex gap-0.5">
                <button onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1))} className="p-1 hover:bg-accent rounded">
                  <ChevronLeft className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
                <button onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1))} className="p-1 hover:bg-accent rounded">
                  <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-0.5 text-center text-[10px]">
              {weekdayLetters(1).map((d, i) => (
                <div key={i} className="py-1 text-muted-foreground/70">{d}</div>
              ))}
              {/* Past days stay selectable: a due date that has already passed is
                  a fact to record (imported backlog, deadline missed), not a typo.
                  The app treats overdue as first-class - it has an Overdue section,
                  badge and filter - and the inline quick-add row always allowed it. */}
              {getCalendarDays().map((day, i) => (
                <button
                  key={i}
                  disabled={!day}
                  onClick={() => day && selectDate(day)}
                  className={`py-1 rounded text-xs ${
                    !day ? '' :
                    day.toDateString() === new Date().toDateString() ? 'bg-amber-500 text-black font-medium' :
                    day && dayOf(value) && day.toDateString() === dayOf(value).toDateString() ? 'bg-blue-500 text-white' :
                    'hover:bg-accent text-foreground/80'
                  }`}
                >
                  {day?.getDate()}
                </button>
              ))}
            </div>
          </div>

          {/* Time select */}
          {onTimeChange && (
            <div className="border-t border-border mt-3 pt-3">
              <TimeSelect
                value={timeValue || ''}
                onChange={onTimeChange}
                placeholder={t('common.select_time')}
                step={15}
              />
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DatePicker;
